import Component from '@glimmer/component';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { HdsPageHeader } from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, BreadcrumbsContainer } from 'core/components';
import { v2ResourceDisplayName } from 'core/helpers';
import Form from 'hcp/components/iam/groups/form';

/**
 * `PageGroupsOrganizationEditComponent` is the parent wrapper component for the HCP Groups edit page.
 * It contains the callback function that is passed down to `<Iam::Groups::Forms::EditGroup @save={{this.save}} />` once the edit group form is validated.
 *
 * Usage:
 * ```
 * <Page::Groups::Organization::Edit />
 * ```
 *
 * @class PageGroupsOrganizationEditComponent
 * @argument {Object} organization - The organization object (required).
 */
export default class PageGroupsOrganizationEdit extends Component {
  @service api;
  @service intl;
  @service flashMessages;
  @service router;
  @service userContext;

  get groupDisplayName() {
    return this.args.model.group.displayName;
  }

  get groupFormFields() {
    return {
      name: this.groupDisplayName,
      description: this.args.model.group.description,
    };
  }

  save = task(async (form) => {
    const payload = {
      resourceName: this.args.model.group.resourceName,
      group: {
        resourceId: this.args.model.group.resourceId,
        resourceName: this.args.model.group.resourceName,
        displayName: form.fields.name,
        description: form.fields.description,
      },
      updateMask: {
        paths: ['display_name', 'description'],
      },
    };

    try {
      await this.api.groups.groupsServiceUpdateGroup(
        this.args.model.group.resourceName,
        payload
      );

      const successTitle = this.intl.t(
        'components.page.access-control.groups.detail.edit.success.title'
      );
      this.flashMessages.success(successTitle, {
        content: this.intl.t(
          'components.page.access-control.groups.detail.edit.success.message',
          {
            htmlSafe: true,
            groupName: form.fields.name,
          }
        ),
      });

      // refresh the Groups list and detail routes to update the model
      getOwner(this)
        .lookup('route:cloud.access-control.groups.list')
        ?.refresh();
      getOwner(this)
        .lookup('route:cloud.access-control.groups.detail')
        ?.refresh();

      // transition to the previous route
      return this.router.transitionTo(this.args.model.previousRouteName);
    } catch {
      const errorTitle = this.intl.t(
        'components.page.access-control.groups.detail.edit.error.title'
      );
      this.flashMessages.error(errorTitle, {
        content: this.intl.t(
          'components.page.access-control.groups.detail.edit.error.message',
          {
            htmlSafe: true,
            groupName: this.groupDisplayName,
          }
        ),
      });
    }
  });

  <template>
    <CoreLayoutMain data-test-groups-organization-edit>
      <:header>
        <HdsPageHeader as |P|>
          <P.Title>
            {{t
              "components.page.access-control.groups.detail.edit.title"
              groupName=(v2ResourceDisplayName @model.group.resourceName)
              htmlSafe=true
            }}
          </P.Title>
          <P.Breadcrumb>
            <BreadcrumbsContainer />
          </P.Breadcrumb>
          <P.IconTile @icon="users" @iconSecondary="edit" />
        </HdsPageHeader>
      </:header>
      <:body>
        <Form
          @saveButtonText={{t
            "components.page.access-control.groups.detail.edit.action"
          }}
          @save={{this.save}}
          @isSaveDisabled={{false}}
          @groupFormFields={{this.groupFormFields}}
          @isEditing={{true}}
        />
      </:body>
    </CoreLayoutMain>
  </template>
}
